<script setup lang="ts">
import type { Component } from 'vue'
import type { InfoNoteTone } from '~/features/my-wallet/types/info-note'
import type { SummaryDisplayData } from '~/features/my-wallet/types/summary'
import { I18nT, useI18n } from 'vue-i18n'
import IllustrationEmptyList from '~icons/illustration/empty-list'
import IconBlock from '~icons/material-symbols/block'
import IconInfo from '~icons/material-symbols/info-outline'
import IconLightbulb from '~icons/material-symbols/lightbulb-outline'
import IconStar from '~icons/material-symbols/star-outline'
import IconTrendingUp from '~icons/material-symbols/trending-up'
import IconWarning from '~icons/material-symbols/warning-outline'
import BlockError from '~/features/my-wallet/components/BlockError.vue'
import MilestoneStepper from '~/features/my-wallet/components/charts/MilestoneStepper.vue'
import PerformanceBar from '~/features/my-wallet/components/charts/PerformanceBar.vue'
import CrossTabLink from '~/features/my-wallet/components/CrossTabLink.vue'
import InfoNote from '~/features/my-wallet/components/InfoNote.vue'
import ModelTag from '~/features/my-wallet/components/ModelTag.vue'
import ModelTitle from '~/features/my-wallet/components/ModelTitle.vue'
import SectionCard from '~/features/my-wallet/components/SectionCard.vue'
import StatusChip from '~/features/my-wallet/components/StatusChip.vue'
import { useWalletTabNavigation } from '~/features/my-wallet/composables/useWalletTabNavigation'
import { PERFORMANCE_WINDOW_DAYS, REASON_KEY_PREFIXES } from '~/features/my-wallet/constants/my-wallet'
import { matchContextNote } from '~/features/my-wallet/utils/context-note'
import { resolveReasonKey } from '~/features/my-wallet/utils/my-wallet'

interface Props {
  summary: SummaryDisplayData
  detailsTab: string
  standardTitle?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  standardTitle: false,
})

const emit = defineEmits<{
  retry: []
}>()

const { t, te } = useI18n()

const { goToTab } = useWalletTabNavigation()

type SummaryFooterNote = { tone: InfoNoteTone, icon: Component } & (
  | { layout: 'titled' | 'inline', title: string, description: string }
  | { layout: 'sentence', keypath: string, highlight: string, model: string | null }
)

const footerNote = computed(() => {
  const contextNote = props.summary.contextNote
  if (!contextNote)
    return null

  return matchContextNote<SummaryFooterNote>(contextNote, {
    commercial: () => ({
      layout: 'titled',
      tone: 'success',
      icon: IconStar,
      title: t('my_wallet.summary.card.commercial_title'),
      description: t('my_wallet.summary.card.commercial_description'),
    }),
    disabled: disabledNote => ({
      layout: 'inline',
      tone: 'warning',
      icon: IconWarning,
      title: t('my_wallet.summary.card.disabled_title', { model: disabledNote.model }),
      description: t('my_wallet.summary.card.disabled_reason', {
        reason: t(resolveReasonKey(REASON_KEY_PREFIXES.MODEL_DISABLED, disabledNote.reason, te)),
      }),
    }),
    no_completions: noCompletionsNote => ({
      layout: 'titled',
      tone: 'info',
      icon: IconInfo,
      title: t('my_wallet.no_completions.title', { days: PERFORMANCE_WINDOW_DAYS }),
      description: t('my_wallet.no_completions.description', { model: noCompletionsNote.model }),
    }),
    next: nextNote => ({
      layout: 'sentence',
      tone: 'neutral',
      icon: IconTrendingUp,
      keypath: 'my_wallet.summary.card.next.text',
      highlight: t('my_wallet.summary.card.next.highlight', { count: nextNote.count }, nextNote.count),
      model: nextNote.model,
    }),
    keep: () => ({
      layout: 'sentence',
      tone: 'neutral',
      icon: IconLightbulb,
      keypath: 'my_wallet.summary.card.keep.text',
      highlight: t('my_wallet.summary.card.keep.highlight'),
      model: null,
    }),
    blocked: () => ({
      layout: 'titled',
      tone: 'danger',
      icon: IconBlock,
      title: t('my_wallet.summary.card.blocked_title'),
      description: t('my_wallet.summary.card.blocked_description'),
    }),
  })
})
</script>

<template>
  <SectionCard id="my-wallet-summary-model-card" :title="t('my_wallet.summary.card.title')" :standard-title="standardTitle" elevated>
    <div v-if="summary.variant === 'blocked'" class="summary-card-blocked">
      <IllustrationEmptyList class="summary-card-blocked-image" aria-hidden="true" />
      <div class="summary-card-blocked-text">
        <p class="summary-card-blocked-title">
          {{ t('my_wallet.summary.card.blocked_title') }}
        </p>
        <p class="summary-card-blocked-description">
          {{ t('my_wallet.summary.card.blocked_description') }}
        </p>
      </div>
    </div>

    <template v-else>
      <div class="summary-card-model">
        <div class="summary-card-model-header">
          <p class="summary-card-label">
            {{ t('my_wallet.summary.card.best_model') }}
          </p>
          <StatusChip v-if="summary.variant === 'essential'" id="my-wallet-summary-model-chip" state="DEFAULT" />
          <ModelTag v-else-if="summary.currentModel" :model="summary.currentModel" />
        </div>
        <button
          v-if="summary.currentModel"
          id="my-wallet-summary-model-split"
          type="button"
          class="summary-card-model-split"
          @click="goToTab(detailsTab)"
        >
          <span class="sr-only">{{ t('my_wallet.summary.card.model_button_prefix', { model: summary.currentModel }) }}</span>
          <span class="summary-card-model-title">
            <ModelTitle :model="summary.currentModel" />
          </span>
          <span class="summary-card-model-values">
            <span class="summary-card-model-title">
              {{ t('my_wallet.models.split', { upfront: summary.upfrontPercentage, after: summary.afterCompletionPercentage }) }}
            </span>
            <span class="summary-card-model-caption">
              {{ t('my_wallet.models.split_caption') }}
            </span>
          </span>
        </button>
      </div>

      <hr class="summary-card-divider">

      <div v-if="summary.variant === 'novice'" class="summary-card-section">
        <div class="summary-card-group">
          <p class="summary-card-label">
            {{ t('my_wallet.summary.card.novice_label') }}
          </p>
          <template v-if="summary.novice">
            <p class="summary-card-value">
              {{ t('my_wallet.summary.card.novice_value', { completed: summary.novice.completed, total: summary.novice.required }) }}
            </p>
            <MilestoneStepper
              size="small"
              :completed="summary.novice.completed"
              :total="summary.novice.required"
              :label="t('my_wallet.receiving_model.novice.stepper_aria', { completed: summary.novice.completed, total: summary.novice.required })"
            />
          </template>
          <BlockError v-else id="my-wallet-summary-novice-error" @retry="emit('retry')" />
        </div>
        <InfoNote tone="info">
          <template #title>
            <p><strong>{{ summary.novice?.isDone ? t('my_wallet.summary.card.novice_alert_title_done') : t('my_wallet.summary.card.novice_alert_title') }}</strong></p>
          </template>
          <p>{{ t('my_wallet.summary.card.novice_alert_description') }}</p>
        </InfoNote>
      </div>

      <div v-else-if="summary.variant === 'performance'" class="summary-card-section">
        <div class="summary-card-group">
          <p class="summary-card-label">
            {{ t('my_wallet.summary.card.performance_label') }}
          </p>
          <div v-if="summary.hasNoCompletions" class="summary-card-performance">
            <p class="summary-card-value">
              {{ t('my_wallet.no_completions.value') }}
            </p>
            <p class="summary-card-model-caption">
              {{ t('my_wallet.no_completions.caption', { days: PERFORMANCE_WINDOW_DAYS }) }}
            </p>
          </div>
          <div v-else-if="summary.percentage !== null" class="summary-card-performance">
            <p class="summary-card-value">
              {{ t('my_wallet.summary.card.performance_value', { percentage: summary.percentage }) }}
            </p>
            <p class="summary-card-model-caption">
              {{ t('my_wallet.summary.card.performance_caption', { days: PERFORMANCE_WINDOW_DAYS }) }}
            </p>
          </div>
          <p v-else class="summary-card-value">
            {{ t('my_wallet.receiving_model.card.unavailable') }}
          </p>
          <PerformanceBar
            v-if="summary.thresholds && !summary.hasNoCompletions"
            variant="compact"
            :percentage="summary.percentage"
            :thresholds="summary.thresholds"
          />
        </div>

        <InfoNote v-if="footerNote?.layout === 'titled'" :tone="footerNote.tone" :icon="footerNote.icon">
          <template #title>
            <p><strong>{{ footerNote.title }}</strong></p>
          </template>
          <p>{{ footerNote.description }}</p>
        </InfoNote>
        <InfoNote v-else-if="footerNote?.layout === 'inline'" :tone="footerNote.tone" :icon="footerNote.icon">
          <p><strong>{{ footerNote.title }}</strong><br>{{ footerNote.description }}</p>
        </InfoNote>
        <InfoNote v-else-if="footerNote?.layout === 'sentence'" :tone="footerNote.tone" :icon="footerNote.icon">
          <I18nT :keypath="footerNote.keypath" tag="p">
            <template #highlight>
              <strong>{{ footerNote.highlight }}</strong>
            </template>
            <template v-if="footerNote.model" #model>
              {{ footerNote.model }}
            </template>
          </I18nT>
        </InfoNote>
      </div>
    </template>

    <template #footer>
      <CrossTabLink
        id="my-wallet-summary-model-details"
        :tab="detailsTab"
        :label="t('my_wallet.summary.card.see_details')"
      />
      <p class="summary-card-updated">
        {{ t('my_wallet.updated_at', summary.updatedAt) }}
      </p>
    </template>
  </SectionCard>
</template>

<style scoped lang="scss">
@use '../../styles/typography';

.summary-card-blocked {
  @apply flex flex-col flex-1 items-center justify-center gap-4 text-center;

  .summary-card-blocked-image {
    @apply w-24 h-24;
  }

  .summary-card-blocked-text {
    @apply flex flex-col gap-1;
  }

  .summary-card-blocked-title {
    @apply fonts-heading-h4 text-neutral-low-dark;
    line-height: 1.5;
  }

  .summary-card-blocked-description {
    @apply fonts-body-medium-regular text-brand-primary-medium;
    line-height: 1.5;
  }
}

.summary-card-model {
  @apply flex flex-col gap-2 pb-2 w-full;
}

.summary-card-model-header {
  @apply flex items-center justify-between gap-2 w-full;
}

.summary-card-label {
  @apply fonts-body-small-bold uppercase text-neutral-low-light;
  line-height: 1.5;
}

.summary-card-model-split {
  @apply flex flex-wrap items-center justify-between gap-2 w-full min-h-16 text-left cursor-pointer;
  @apply rounded-md;

  &:focus-visible {
    @apply outline-none ring-2 ring-neutral-low-dark ring-offset-2;
  }

  > .summary-card-model-title {
    flex: 1 1 8rem;
  }
}

.summary-card-model-title,
.summary-card-value {
  @apply text-neutral-low-dark;
  @include typography.title-20;
}

.summary-card-model-values {
  @apply flex flex-col items-end gap-1 flex-shrink-0 ml-auto text-right;
}

.summary-card-model-caption {
  @apply fonts-body-medium-regular text-brand-primary-medium;
  line-height: 1.5;
}

.summary-card-divider {
  @apply w-full h-px m-0 border-0 bg-neutral-high-medium;
}

.summary-card-section {
  @apply flex flex-col gap-8 pt-1 pb-2 w-full;
}

.summary-card-group {
  @apply flex flex-col gap-4 w-full;
}

.summary-card-performance {
  @apply flex flex-col gap-1;
}

.summary-card-updated {
  @apply ml-auto text-right;
  @include typography.updated-at;
}
</style>
